import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { apiService } from '../services/api';

interface AuthContextType {
  isAuthenticated: boolean;
  username: string | null;
  isLoading: boolean;
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [username, setUsername] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Verify token on mount
  const verifyAuth = useCallback(async () => {
    const token = localStorage.getItem('authToken');
    const storedUsername = localStorage.getItem('username');
    
    if (!token) {
      setIsAuthenticated(false);
      setUsername(null);
      setIsLoading(false);
      return;
    }

    try {
      const response = await apiService.verifyToken();
      
      if (response.valid) {
        setIsAuthenticated(true);
        setUsername(response.username || storedUsername);
      } else {
        // Token invalid or expired - clear auth state
        localStorage.removeItem('authToken');
        localStorage.removeItem('username');
        setIsAuthenticated(false);
        setUsername(null);
      }
    } catch (error) {
      console.error('Auth verification failed:', error);
      // Clear auth state on error
      localStorage.removeItem('authToken');
      localStorage.removeItem('username');
      setIsAuthenticated(false);
      setUsername(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    verifyAuth();
  }, [verifyAuth]);

  const login = async (inputUsername: string, inputPassword: string): Promise<boolean> => {
    try {
      const response = await apiService.login(inputUsername, inputPassword);
      
      if (response.success && response.token) {
        // Store token and username
        localStorage.setItem('authToken', response.token);
        localStorage.setItem('username', response.username || inputUsername);
        
        setIsAuthenticated(true);
        setUsername(response.username || inputUsername);
        
        console.log('Login successful for user:', response.username);
        return true;
      }
      
      console.log('Login failed:', response.message);
      return false;
    } catch (error) {
      console.error('Login error:', error);
      return false;
    }
  };

  const logout = () => {
    // Clear auth state
    localStorage.removeItem('authToken');
    localStorage.removeItem('username');
    localStorage.removeItem('isAuthenticated'); // Clear legacy key if exists
    
    setIsAuthenticated(false);
    setUsername(null);
    
    console.log('User logged out');
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, username, isLoading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
